import React, { useCallback, useRef, useState } from 'react';
import { PITCH_FREQS, PITCH_HL_MIN, PITCH_HL_MAX } from './tinnitogramLogic';

/**
 * PitchMatchGraph — audiogram-style graph for tinnitus pitch match.
 *
 * • X = Frequency (7 columns 125..8K Hz), Y = HL (-10..120 inverted).
 * • Threshold: open circles connected by a line (RE red, LE blue).
 * • Pitch Match: bold X marker at (match_freq, match_hl).
 * • Two plot modes toggle-able above the graph:
 *     'threshold'  → clicks set the threshold at nearest freq column
 *     'match'      → clicks set the pitch-match marker at any freq
 * • Ear toggle picks Right vs Left target.
 */
const PitchMatchGraph = ({
  pitch,          // {right:{thresholds,match_freq,match_hl}, left:{...}}
  plotEar = 'right',
  plotMode = 'threshold',
  onSetEar,
  onSetMode,
  onSetThreshold, // (ear, freq, dbHL) => void
  onSetMatch,     // (ear, freq, dbHL) => void
  readOnly = false,
  width = 460, height = 260,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 42, marginR = 12, marginT = 30, marginB = 34;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;

  const xScale = useCallback((i) => marginL + (i * plotW) / (PITCH_FREQS.length - 1), [plotW]);
  const yScale = useCallback((hl) => marginT + ((hl - PITCH_HL_MIN) * plotH) / (PITCH_HL_MAX - PITCH_HL_MIN), [plotH]);
  const yInv = useCallback((py) => {
    const raw = PITCH_HL_MIN + ((py - marginT) * (PITCH_HL_MAX - PITCH_HL_MIN)) / plotH;
    return Math.max(PITCH_HL_MIN, Math.min(PITCH_HL_MAX, Math.round(raw / 5) * 5));
  }, [plotH]);

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 8 || p.x > width - marginR + 8) return null;
    let bestIdx = 0, bestDx = Infinity;
    PITCH_FREQS.forEach((_, i) => {
      const dx = Math.abs(xScale(i) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return { freq: PITCH_FREQS[bestIdx], idx: bestIdx, hl: yInv(Math.max(marginT, Math.min(height - marginB, p.y))) };
  };

  const handleClick = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    if (plotMode === 'threshold') onSetThreshold && onSetThreshold(plotEar, s.freq, s.hl);
    else onSetMatch && onSetMatch(plotEar, s.freq, s.hl);
  };

  const hlTicks = [-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];
  const earColor = (ear) => ear === 'right' ? '#dc2626' : '#2563eb';

  const buildEarPath = (earObj) => {
    if (!earObj?.thresholds) return '';
    let d = '';
    PITCH_FREQS.forEach((f, i) => {
      const v = earObj.thresholds[String(f)];
      if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) {
        d += `${d ? 'L' : 'M'}${xScale(i)},${yScale(Number(v))} `;
      }
    });
    return d.trim();
  };

  const earThresholdMarks = (earObj, color) => {
    if (!earObj?.thresholds) return null;
    return PITCH_FREQS.map((f, i) => {
      const v = earObj.thresholds[String(f)];
      if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
      return <circle key={`t-${color}-${f}`} cx={xScale(i)} cy={yScale(Number(v))} r="5" fill="#ffffff" stroke={color} strokeWidth="2" />;
    });
  };

  const pitchMarker = (earObj, color) => {
    if (!earObj?.match_freq || !Number.isFinite(Number(earObj.match_freq))) return null;
    const freq = Number(earObj.match_freq);
    const idx = PITCH_FREQS.findIndex((f) => f === freq);
    const cx = idx >= 0
      ? xScale(idx)
      : marginL + (Math.log2(freq / PITCH_FREQS[0]) / Math.log2(PITCH_FREQS[PITCH_FREQS.length - 1] / PITCH_FREQS[0])) * plotW;
    const cy = earObj.match_hl !== null && earObj.match_hl !== undefined ? yScale(Number(earObj.match_hl)) : yScale(30);
    return (
      <g>
        <line x1={cx - 6} y1={cy - 6} x2={cx + 6} y2={cy + 6} stroke={color} strokeWidth="3" strokeLinecap="round" />
        <line x1={cx + 6} y1={cy - 6} x2={cx - 6} y2={cy + 6} stroke={color} strokeWidth="3" strokeLinecap="round" />
      </g>
    );
  };

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="pitch-controls">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot ear:</span>
          {['right', 'left'].map((e) => (
            <button key={e} type="button" data-testid={`pitch-ear-${e}`}
              onClick={() => onSetEar && onSetEar(e)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${plotEar === e ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
              style={plotEar === e ? { backgroundColor: earColor(e) } : {}}
            >{e === 'right' ? 'RE' : 'LE'}</button>
          ))}
          <span className="mx-2 h-4 w-px bg-slate-300" />
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot:</span>
          {['threshold', 'match'].map((m) => (
            <button key={m} type="button" data-testid={`pitch-mode-${m}`}
              onClick={() => onSetMode && onSetMode(m)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${plotMode === m ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600'}`}
            >{m === 'threshold' ? 'Threshold' : 'Pitch Match'}</button>
          ))}
        </div>
      )}
      {/* HTML legend — placed above the SVG so items never overlap regardless of container width. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mb-1 text-[10px] text-slate-600" data-testid="pitch-legend">
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden><circle cx="6" cy="6" r="4" fill="#ffffff" stroke="#dc2626" strokeWidth="1.5" /></svg>
          <span style={{ color: '#dc2626' }} className="font-semibold">Right Ear (Threshold)</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden>
            <line x1="2" y1="2" x2="10" y2="10" stroke="#dc2626" strokeWidth="2" />
            <line x1="10" y1="2" x2="2" y2="10" stroke="#dc2626" strokeWidth="2" />
          </svg>
          <span style={{ color: '#dc2626' }} className="font-semibold">Right Pitch Match</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden><circle cx="6" cy="6" r="4" fill="#ffffff" stroke="#2563eb" strokeWidth="1.5" /></svg>
          <span style={{ color: '#2563eb' }} className="font-semibold">Left Ear (Threshold)</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden>
            <line x1="2" y1="2" x2="10" y2="10" stroke="#2563eb" strokeWidth="2" />
            <line x1="10" y1="2" x2="2" y2="10" stroke="#2563eb" strokeWidth="2" />
          </svg>
          <span style={{ color: '#2563eb' }} className="font-semibold">Left Pitch Match</span>
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="pitch-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Grid */}
        {hlTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke={t === 0 ? '#94a3b8' : '#e2e8f0'} strokeWidth={t === 0 ? 0.8 : 0.5} />
            {t % 10 === 0 && <text x={marginL - 6} y={yScale(t) + 3} fontSize="8" fill="#64748b" textAnchor="end">{t}</text>}
          </g>
        ))}
        {PITCH_FREQS.map((f, i) => (
          <g key={f}>
            <line x1={xScale(i)} x2={xScale(i)} y1={marginT} y2={height - marginB} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(i)} y={height - marginB + 12} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">
              {f >= 1000 ? `${f / 1000}K` : f}
            </text>
          </g>
        ))}
        <text x={marginL - 32} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 32}, ${marginT + plotH / 2})`} textAnchor="middle">Hearing Level (dB HL)</text>
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Frequency (Hz)</text>
        {/* Ear lines + markers */}
        {['left', 'right'].map((ear) => (
          <g key={ear}>
            {(() => {
              const path = buildEarPath(pitch?.[ear]);
              return path ? <path d={path} fill="none" stroke={earColor(ear)} strokeWidth="1.8" /> : null;
            })()}
            {earThresholdMarks(pitch?.[ear], earColor(ear))}
            {pitchMarker(pitch?.[ear], earColor(ear))}
          </g>
        ))}
        {/* Hover ghost */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="pitch-hover">
            <line x1={xScale(hover.idx)} x2={xScale(hover.idx)} y1={marginT} y2={height - marginB} stroke={earColor(plotEar)} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            {plotMode === 'threshold'
              ? <circle cx={xScale(hover.idx)} cy={yScale(hover.hl)} r="5" fill="#ffffff" stroke={earColor(plotEar)} strokeWidth="1.5" opacity="0.85" />
              : (
                <g>
                  <line x1={xScale(hover.idx) - 6} y1={yScale(hover.hl) - 6} x2={xScale(hover.idx) + 6} y2={yScale(hover.hl) + 6} stroke={earColor(plotEar)} strokeWidth="3" opacity="0.85" />
                  <line x1={xScale(hover.idx) + 6} y1={yScale(hover.hl) - 6} x2={xScale(hover.idx) - 6} y2={yScale(hover.hl) + 6} stroke={earColor(plotEar)} strokeWidth="3" opacity="0.85" />
                </g>
              )
            }
            <rect x={xScale(hover.idx) + 6} y={Math.max(marginT, yScale(hover.hl) - 10)} width="60" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={xScale(hover.idx) + 8} y={Math.max(marginT, yScale(hover.hl) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.freq >= 1000 ? `${hover.freq / 1000}K` : hover.freq} · {hover.hl} dB
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default PitchMatchGraph;
